import { Link } from 'react-router-dom';
import ArticleLayout from '@/components/shared/ArticleLayout';

const SalaryPercentilesGuide = () => (
  <ArticleLayout
    slug="how-to-read-salary-percentiles"
    title="How to Read Salary Percentiles (and Why the Average Is Misleading)"
    description="Salary percentiles tell you far more than a single average ever could. Learn what the 10th, 25th, 50th, 75th, and 90th percentiles actually mean, why mean and median often disagree, and how to use the full distribution to judge a job offer."
    category="Salary Data"
    datePublished="2026-01-15"
    dateModified="2026-06-01"
    readTime="9 min read"
    faqItems={[
      {
        question: 'What does the 90th percentile salary mean?',
        answer:
          'The 90th percentile is the wage that only 10% of workers in an occupation earn more than. It typically reflects highly experienced workers, those in high-paying industries or regions, or those in specialized roles. It is a realistic long-term ceiling for most people in the field, not a typical offer.',
      },
      {
        question: 'Why is the median salary lower than the average (mean) salary?',
        answer:
          'Wages cannot go below zero but have no upper limit, so a small number of very high earners pulls the mean upward while leaving the median unchanged. When the mean is well above the median, the occupation has a long right tail of high earners; the median is usually the better measure of what a typical worker earns.',
      },
      {
        question: 'Which percentile should I compare my salary to?',
        answer:
          'Start with the median (50th percentile) for your occupation and state. If you have several years of experience, specialized skills, or work in a high-paying industry, comparing against the 75th percentile is more realistic. Entry-level workers should look at the range between the 10th and 25th percentiles.',
      },
      {
        question: 'Where does Wage Atlas percentile data come from?',
        answer:
          'All percentile figures on Wage Atlas come from the U.S. Bureau of Labor Statistics Occupational Employment and Wage Statistics (OEWS) program, which surveys roughly 1.1 million business establishments to estimate wages for around 800 occupations at the national, state, and metro level.',
      },
    ]}
    relatedLinks={[
      {
        title: 'Cost of Living vs. Salary',
        description: 'Why a smaller paycheck can be the better deal once regional prices enter the math.',
        href: '/insights/cost-of-living-vs-salary',
      },
      {
        title: 'The Salary Negotiation Playbook',
        description: 'Turn percentile data into a concrete, defensible number in your next negotiation.',
        href: '/insights/salary-negotiation-guide',
      },
      {
        title: 'Search Salary Data',
        description: 'Look up full percentile distributions for roughly 800 occupations in every state.',
        href: '/search',
      },
      {
        title: 'Our Methodology',
        description: 'How we process BLS data, what we adjust, and what the numbers can and cannot tell you.',
        href: '/methodology',
      },
    ]}
  >
    <p>
      Type almost any job title into a search engine and you will get a single number: "the average
      software developer earns $132,000," "the average nurse earns $94,000." That number is easy to
      quote and almost useless on its own. It hides the difference between a first-year hire in
      Mississippi and a twenty-year veteran in San Francisco, between a small nonprofit and a Fortune
      500 employer. If you make career or negotiation decisions based on a single average, you are
      navigating with half a map.
    </p>
    <p>
      The fix is to read the full wage <em>distribution</em> — the percentiles. The Bureau of Labor
      Statistics publishes five points on that distribution for nearly every occupation it tracks,
      and once you know how to read them, you can answer much sharper questions: What does
      entry-level actually pay here? What is a realistic ceiling? Is this offer genuinely good, or
      just not bad?
    </p>

    <h2>What each percentile actually means</h2>
    <p>
      A percentile is a rank, not a grade. The 25th percentile wage is the amount that 25% of
      workers in an occupation earn <em>less</em> than and 75% earn more than. Concretely, the five
      published points break down like this:
    </p>
    <ul>
      <li>
        <strong>10th percentile (p10).</strong> Only one in ten workers earns less than this. Think
        of it as the floor for the occupation: trainees, brand-new hires, part-year workers, and
        jobs in the lowest-paying regions and industries cluster here.
      </li>
      <li>
        <strong>25th percentile (p25).</strong> The lower-middle of the field. A realistic
        benchmark for someone with limited experience or working in a lower-cost area.
      </li>
      <li>
        <strong>50th percentile — the median.</strong> Half earn more, half earn less. This is the
        single most honest answer to "what does this job pay?"
      </li>
      <li>
        <strong>75th percentile (p75).</strong> The upper-middle. Experienced workers, those in
        high-paying industries, or those in expensive metro areas typically sit here.
      </li>
      <li>
        <strong>90th percentile (p90).</strong> Only one in ten earns more. This is a realistic
        long-run ceiling for most people in the occupation — not the absolute maximum, but the
        point beyond which you are usually looking at management, rare specialization, or unusually
        generous employers.
      </li>
    </ul>
    <p>
      The distance between p10 and p90 is itself a piece of information: it tells you how much the
      occupation rewards experience, location, and industry choice. Some occupations are tightly
      compressed — a postal carrier at the 90th percentile earns perhaps 50% more than one at the
      10th. Others are wide open: a financial advisor at p90 can earn four or five times the p10
      wage. A wide spread means your decisions (where you work, which industry, how you negotiate)
      matter enormously. A narrow spread means the pay is largely set by the occupation itself.
    </p>

    <h2>Mean vs. median: why the two "averages" disagree</h2>
    <p>
      Most salary pages on Wage Atlas show both a <strong>mean</strong> (the arithmetic average)
      and a <strong>median</strong>. They are rarely equal, and the gap is informative. Wages have
      a hard floor — nobody earns a negative salary — but no ceiling. A handful of workers earning
      $400,000 in an occupation where most earn $80,000 pulls the mean up substantially while
      leaving the median untouched.
    </p>
    <p>
      As a rule of thumb: when the mean sits well above the median, the occupation has a long right
      tail of high earners, and the median is the better description of a typical paycheck. When
      mean and median are close, pay is evenly distributed and either number is fine. You will
      almost never see the mean <em>below</em> the median in wage data, because the skew nearly
      always runs toward high earners.
    </p>
    <p>
      This is also why "average salary" headlines mislead. The average is the number most inflated
      by outliers — exactly the workers whose situation probably does not resemble yours.
    </p>

    <h2>A worked example</h2>
    <p>
      Suppose you are evaluating an offer as a registered nurse, and the state-level distribution
      looks like this (figures rounded for clarity):
    </p>
    <ul>
      <li>10th percentile: $66,000</li>
      <li>25th percentile: $76,000</li>
      <li>Median: $89,000</li>
      <li>75th percentile: $104,000</li>
      <li>90th percentile: $121,000</li>
    </ul>
    <p>
      An offer of $84,000 now has context. It sits between the 25th percentile and the median — a
      below-typical wage for the state. For a new graduate, that may be entirely reasonable; for a
      nurse with eight years of ICU experience, it is a signal to negotiate or keep looking. Without
      the percentiles, "$84,000" is just a number that sounds large. With them, it is a position on
      a map.
    </p>
    <p>
      Notice what else the distribution tells you: the spread from p10 to p90 is under 2x, which is
      relatively compressed. Nursing pay is structured by employers and union scales; the biggest
      lever for a nurse who wants to move up the distribution is usually <em>location</em> and
      <em>shift/specialty</em>, not negotiation alone. Compare that with sales occupations, where
      the p90 can be several multiples of the p10 and individual performance dominates.
    </p>

    <h2>How experience, industry, and geography move you along the curve</h2>
    <p>
      The OEWS data that powers Wage Atlas does not break wages down by years of experience — no
      government survey does at this scale. But the percentile distribution is a reliable proxy,
      because the same forces show up in it again and again:
    </p>
    <p>
      <strong>Experience</strong> moves most workers from around p10–p25 at entry toward p50–p75 at
      mid-career. Reaching p90 usually requires something beyond tenure: a leadership role, a
      high-paying industry, or an expensive labor market.
    </p>
    <p>
      <strong>Industry</strong> can matter as much as the job title. The same database
      administrator earns very different wages in local government, in a hospital system, and at a
      software company. On any Wage Atlas salary page you can see the industry breakdown for an
      occupation — if your industry pays below the occupation's median, switching industries is
      often a larger raise than any internal promotion.
    </p>
    <p>
      <strong>Geography</strong> shifts the entire curve. A p50 wage in one state can exceed a p75
      wage in another. That is precisely why we publish state and metro distributions rather than a
      single national figure — and why comparing your salary to a national average can leave you
      either falsely satisfied or needlessly alarmed. Our{' '}
      <Link to="/best-states">best states tool</Link> ranks every state for a given occupation, and
      the <Link to="/insights/cost-of-living-vs-salary">cost-of-living companion article</Link>{' '}
      explains how to adjust those comparisons for regional prices.
    </p>

    <h2>Reading percentiles when you evaluate an offer</h2>
    <p>A practical sequence we recommend:</p>
    <ol>
      <li>
        <strong>Find your occupation and state</strong> using the{' '}
        <Link to="/search">salary search</Link>. Use the state where the job is located, not where
        you live now.
      </li>
      <li>
        <strong>Anchor on the median.</strong> This is "typical" for the occupation in that market.
      </li>
      <li>
        <strong>Place yourself honestly.</strong> Entry level with no specialization? Benchmark
        p10–p25. Several years in with in-demand skills? p50–p75. Deep specialist or proven
        leader? p75–p90.
      </li>
      <li>
        <strong>Check the industry mix.</strong> If the employer is in an industry that pays above
        the occupation median, expect the offer to be above median too — and treat a median offer
        as low.
      </li>
      <li>
        <strong>Adjust for cost of living</strong> if you are comparing offers across states. The
        RPP toggle on our salary pages does this in one click.
      </li>
    </ol>
    <p>
      One caution: percentile data describes <em>all current workers</em>, including people hired
      years ago at wages that have since grown. In fast-moving labor markets, new-hire offers can
      run ahead of the published distribution. Treat OEWS percentiles as a reliable floor and
      structure, then layer on current job-posting data for the final read.
    </p>

    <h2>The bottom line</h2>
    <p>
      A single average flattens an occupation into one number and erases everything that makes your
      situation specific. The five percentiles restore that information: where the floor is, where
      the ceiling is, what typical looks like, and how much room the occupation gives you to climb.
      Every salary page on Wage Atlas shows the full distribution for this reason. Learn to read
      it, and you will never again have to ask whether a number is "good" — you will be able to see
      exactly where it lands.
    </p>
  </ArticleLayout>
);

export default SalaryPercentilesGuide;
